import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Grid, TextField } from "@material-ui/core";
import { Autocomplete } from "@mui/material";
import { Add, Description } from "@material-ui/icons";

import CardTable from "components/CardTable/CardTable";
import { TableInputs } from "components/CardTable/TableInputs";
import RegularButton from "components/CustomButtons/Button";
import { useToggle } from "hooks/useToggle";
import { useSearch } from "hooks/useSearch";
import { useRuleValidator } from "hooks/useRuleValidator";
import { PERMISSIONS } from "router/permissions";

import {
  getMonthlyClosings,
  setMonthlyClosingFilters,
} from "redux/actions/monthlyClosing/monthlyClosingActions";
import { downloadReceipt } from "redux/actions/retentionOutputs/retentionOutputsActions";
import { columns } from "./columns";
import { MonthlyClosingForm } from "./MonthlyClosingForm";
import { MONTHLY_CLOSING_LABELS } from "./labels";

import { monthsDictionary } from "helpers/dictionary";

const { title, filters, messages } = MONTHLY_CLOSING_LABELS;

const currentYear = new Date().getFullYear();
const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - i);

export default function MonthlyClosingView() {
  const dispatch = useDispatch();
  const { setSnackbar } = useSearch();
  const [detailOpen, setDetailOpen] = useToggle();
  const [createOpen, setCreateOpen] = useToggle();
  const [selectedClosing, setSelectedClosing] = useState(null);

  const { closings, station, month, year } = useSelector(
    (state) => state.monthlyClosing
  );
  const { isLoadingComp } = useSelector(({ search }) => search);
  const canCreate = useRuleValidator(PERMISSIONS.WITHHOLDINGS_CLOSING_CREATE);

  const handleView = (row) => {
    setSelectedClosing(row);
    setDetailOpen();
  };

  const handleDownloadReceipt = (row) => dispatch(downloadReceipt(row.id));

  const handleMonthChange = (_, value) => {
    dispatch(setMonthlyClosingFilters("month", value?.value || null));
  };

  const handleYearChange = (_, value) => {
    dispatch(setMonthlyClosingFilters("year", value));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!station?.id) {
      setSnackbar(messages.selectStation, "warning");
      return;
    }
    dispatch(getMonthlyClosings(station, month, year));
  };

  const selectedMonth = monthsDictionary.find((m) => m.value === month) || null;

  return (
    <>
      <CardTable
        data={closings}
        columns={columns({ onView: handleView, onDownloadReceipt: handleDownloadReceipt })}
        hasHeader
        blockBtn
        title={title}
        icon={<Description />}
        isLoading={isLoadingComp?.loadingMonthlyClosings}
        tableContent={
          <TableInputs
            stations
            reducer="monthlyClosing"
            dispatchAction={setMonthlyClosingFilters}
            submitAction={handleSubmit}
            loading={isLoadingComp?.loadingMonthlyClosings}
          >
            <Grid item xs={12} sm={2}>
              <Autocomplete
                options={monthsDictionary}
                getOptionLabel={(option) => option.label}
                value={selectedMonth}
                onChange={handleMonthChange}
                renderInput={(params) => (
                  <TextField {...params} label={filters.month} variant="outlined" size="small" />
                )}
                noOptionsText={filters.noOptions}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <Autocomplete
                options={yearOptions}
                getOptionLabel={(option) => String(option)}
                value={year}
                onChange={handleYearChange}
                disableClearable
                renderInput={(params) => (
                  <TextField {...params} label={filters.year} variant="outlined" size="small" />
                )}
                noOptionsText={filters.noOptions}
              />
            </Grid>
          </TableInputs>
        }
        hasControl
        controls={
          canCreate && (
            <RegularButton round justIcon color="info" onClick={setCreateOpen}>
              <Add style={{ margin: 0 }} />
            </RegularButton>
          )
        }
      />

      <MonthlyClosingForm
        open={detailOpen}
        onClose={setDetailOpen}
        data={selectedClosing}
      />

      <MonthlyClosingForm
        open={createOpen}
        onClose={setCreateOpen}
        data={null}
        isClose
      />
    </>
  );
}
